'use client';

// Modal padrão do Trakd (visual "Novo incidente") sobre Radix Dialog.
// Portal pro body + focus-trap + ESC + scroll-lock (Radix). Cobre a página toda.
//
// Uso:
//   <Modal open={open} onOpenChange={setOpen} title="..." icon={<TbX/>} tone="danger"
//          footer={<><button>Cancelar</button><button>Confirmar</button></>}>
//     ...conteúdo do corpo...
//   </Modal>

import * as Dialog from '@radix-ui/react-dialog';
import type { ReactElement, ReactNode } from 'react';
import { TbX } from 'react-icons/tb';

import { cn } from '@/lib/utils';

export type ModalTone = 'brand' | 'info' | 'warning' | 'danger';
export type ModalSize = 'sm' | 'md' | 'lg';

const TONE_CHIP: Record<ModalTone, string> = {
  brand: 'bg-violet/15 text-violet',
  info: 'bg-sky-400/12 text-sky-300',
  warning: 'bg-amber-400/12 text-amber-300',
  danger: 'bg-red-500/12 text-red-300',
};
const SIZE_MAX: Record<ModalSize, string> = {
  sm: 'max-w-[420px]',
  md: 'max-w-[520px]',
  lg: 'max-w-[680px]',
};

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  tone?: ModalTone;
  size?: ModalSize;
  footer?: ReactNode;
  hideClose?: boolean;
  className?: string;
  children: ReactNode;
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  icon,
  tone = 'brand',
  size = 'md',
  footer,
  hideClose,
  className,
  children,
}: ModalProps): ReactElement {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 fixed inset-0 z-[1000] bg-[rgba(5,6,10,0.55)] backdrop-blur-md" />
        <Dialog.Content
          aria-describedby={description ? undefined : undefined}
          // Popovers internos (ex: CustomSelect) são portalados pro body com
          // [data-floating-popover] — pro Radix eles contam como "fora" do
          // dialog; sem estes guards, clicar numa opção fecharia o modal e
          // Escape fecharia o modal junto com o popover.
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest('[data-floating-popover]')) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (document.querySelector('[data-floating-popover]')) e.preventDefault();
          }}
          className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
            'fixed top-1/2 left-1/2 z-[1000] flex max-h-[88vh] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[20px] border border-[rgba(222,219,246,0.14)] bg-[linear-gradient(180deg,#15131f,#0f0d18)] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)] duration-200',
            SIZE_MAX[size],
            className,
          )}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute -top-20 -right-10 h-52 w-52 rounded-full bg-[#9061FF] opacity-[0.12] blur-[50px]"
          />

          <div className="border-border-subtle relative flex items-center justify-between gap-3 border-b px-5 py-[18px]">
            <div className="flex min-w-0 items-center gap-3">
              {icon && (
                <span
                  className={cn(
                    'grid h-8 w-8 flex-none place-items-center rounded-[10px]',
                    TONE_CHIP[tone],
                  )}
                >
                  {icon}
                </span>
              )}
              <div className="min-w-0">
                {title ? (
                  <Dialog.Title className="truncate text-base font-bold">{title}</Dialog.Title>
                ) : (
                  <Dialog.Title className="sr-only">Modal</Dialog.Title>
                )}
                {description && (
                  <Dialog.Description className="text-ink-muted mt-0.5 text-xs">
                    {description}
                  </Dialog.Description>
                )}
              </div>
            </div>
            {!hideClose && (
              <Dialog.Close
                className="text-ink-muted hover:text-ink flex-none transition-colors"
                aria-label="Fechar"
              >
                <TbX size={20} strokeWidth={1.8} />
              </Dialog.Close>
            )}
          </div>

          <div className="scrollbar-ghost relative flex flex-col gap-4 overflow-y-auto px-5 py-[18px]">
            {children}
          </div>

          {footer && (
            <div className="border-border-subtle flex flex-wrap items-center justify-end gap-2.5 border-t px-5 py-4">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
